import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical } from "lucide-react";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useServerFn } from "@tanstack/react-start";
import { sendDirectAdvisorInvite } from "@/lib/invites.functions";
import { resendUserAccessLink, deleteUserAccount } from "@/lib/userAdmin.functions";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export const Route = createFileRoute("/admin/agents")({
  head: () => ({
    meta: [
      { title: "Manage Strategists — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Manage Strategists" nav={ADMIN_NAV}>
        <AgentsTable />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface AgentRow {
  id: string;
  user_id: string;
  status: string;
  hire_date: string | null;
  license_states: string[] | null;
  full_name: string;
  email: string;
  mtd: number;
  ytd: number;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const STATUS_PILL: Record<string, string> = {
  pending: "bg-bronze-400/20 text-bronze-600",
  active: "bg-success/15 text-success",
  suspended: "bg-danger/15 text-danger",
};

function AgentsTable() {
  const { user } = useAuth();
  const [rows, setRows] = useState<AgentRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [suspendTarget, setSuspendTarget] = useState<AgentRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<AgentRow | null>(null);
  const [reassignClients, setReassignClients] = useState<Array<{ id: string; full_name: string }>>([]);
  const [reassignTo, setReassignTo] = useState<string>("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const inviteFn = useServerFn(sendDirectAdvisorInvite);
  const resendFn = useServerFn(resendUserAccessLink);
  const deleteFn = useServerFn(deleteUserAccount);
  const [deleteTarget, setDeleteTarget] = useState<AgentRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

    const [agentsRes, profilesRes, policiesRes] = await Promise.all([
      supabase.from("agents").select("id, user_id, status, hire_date, license_states"),
      supabase.from("profiles").select("id, full_name, email"),
      supabase.from("policies").select("agent_id, face_amount, effective_date").gte("effective_date", yearStart),
    ]);

    const profileMap = new Map<string, { full_name: string; email: string }>();
    for (const p of (profilesRes.data ?? []) as Array<{ id: string; full_name: string | null; email: string | null }>) {
      profileMap.set(p.id, { full_name: p.full_name || "—", email: p.email || "" });
    }

    const ytdMap = new Map<string, number>();
    const mtdMap = new Map<string, number>();
    for (const p of (policiesRes.data ?? []) as Array<{ agent_id: string | null; face_amount: number | null; effective_date: string | null }>) {
      if (!p.agent_id) continue;
      const amt = Number(p.face_amount) || 0;
      ytdMap.set(p.agent_id, (ytdMap.get(p.agent_id) ?? 0) + amt);
      if (p.effective_date && p.effective_date >= monthStart) {
        mtdMap.set(p.agent_id, (mtdMap.get(p.agent_id) ?? 0) + amt);
      }
    }

    const merged: AgentRow[] = ((agentsRes.data ?? []) as Array<{
      id: string; user_id: string; status: string; hire_date: string | null; license_states: string[] | null;
    }>).map((a) => ({
      ...a,
      full_name: profileMap.get(a.user_id)?.full_name ?? "—",
      email: profileMap.get(a.user_id)?.email ?? "",
      mtd: mtdMap.get(a.user_id) ?? 0,
      ytd: ytdMap.get(a.user_id) ?? 0,
    }));
    setRows(merged);
  }

  const allStates = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const s of r.license_states ?? []) set.add(s);
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        if (stateFilter !== "all" && !(r.license_states ?? []).includes(stateFilter)) return false;
        if (search && !r.full_name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [rows, statusFilter, stateFilter, search],
  );

  async function approve(row: AgentRow) {
    const { error } = await supabase.from("agents").update({ status: "active" }).eq("id", row.id);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id, action: "admin.user_approved", target_type: "agent", target_id: row.user_id,
    });
    toast.success(`${row.full_name} approved`);
    void load();
  }

  async function suspend(row: AgentRow) {
    const { error } = await supabase.from("agents").update({ status: "suspended" }).eq("id", row.id);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id, action: "admin.user_suspended", target_type: "agent", target_id: row.user_id,
    });
    toast.success(`${row.full_name} suspended`);
    setSuspendTarget(null);
    void load();
  }

  async function invite() {
    if (!inviteEmail) return;
    setInviteSending(true);
    try {
      await inviteFn({ data: { email: inviteEmail, fullName: inviteName || null } });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteName("");
      setInviteOpen(false);
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send invite.");
    } finally {
      setInviteSending(false);
    }
  }

  async function openReassign(row: AgentRow) {
    setReassignTarget(row);
    setReassignTo("");
    const [clientsRes, profRes] = await Promise.all([
      supabase.from("clients").select("id, user_id").eq("assigned_agent_id", row.user_id),
      supabase.from("profiles").select("id, full_name"),
    ]);
    const pm = new Map<string, string>();
    for (const p of (profRes.data ?? []) as Array<{ id: string; full_name: string | null }>) {
      pm.set(p.id, p.full_name || "Unnamed");
    }
    setReassignClients(((clientsRes.data ?? []) as Array<{ id: string; user_id: string }>).map((c) => ({
      id: c.id, full_name: pm.get(c.user_id) ?? "—",
    })));
  }

  async function applyReassign() {
    if (!reassignTarget || !reassignTo || reassignClients.length === 0) return;
    setReassignBusy(true);
    const ids = reassignClients.map((c) => c.id);
    const { error } = await supabase.from("clients").update({ assigned_agent_id: reassignTo }).in("id", ids);
    setReassignBusy(false);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id, action: "admin.clients_reassigned", target_type: "agent",
      target_id: reassignTarget.user_id,
      metadata: { from_agent: reassignTarget.user_id, to_agent: reassignTo, count: ids.length },
    });
    toast.success(`Reassigned ${ids.length} client(s)`);
    setReassignTarget(null);
    void load();
  }

  async function resend(row: AgentRow) {
    try {
      await resendFn({ data: { userId: row.user_id } });
      toast.success(`Access link sent to ${row.full_name || row.email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resend link.");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteFn({ data: { userId: deleteTarget.user_id, kind: "agent" } });
      toast.success(`${deleteTarget.full_name} removed`);
      setDeleteTarget(null);
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete account.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4">
        <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "32px", letterSpacing: "-0.01em" }}>
          Manage <em className="text-bronze-600 italic font-normal">Strategists</em>
        </h2>
        <PrimaryButton onClick={() => setInviteOpen(true)}>Invite Strategist</PrimaryButton>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-40 bg-white border-bronze-600/30"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            {allStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…" className="w-64 bg-white border-bronze-600/30"
        />
      </div>

      <div className="mt-6 bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
        <table className="w-full">
          <thead className="bg-cream-50 border-b border-bronze-600/20">
            <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              <th className="px-4 py-3">Strategist</th>
              <th className="px-4 py-3">Hire Date</th>
              <th className="px-4 py-3">States</th>
              <th className="px-4 py-3 text-right">MTD</th>
              <th className="px-4 py-3 text-right">YTD</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bronze-600/15">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center font-sans text-[13px] text-charcoal-500">No strategists found.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-cream-50/60">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-navy-800 text-cream-50 font-sans font-semibold text-[11px] flex items-center justify-center">
                      {(r.full_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-sans text-[13px] text-charcoal-900">{r.full_name}</p>
                      <p className="font-sans text-[11px] text-charcoal-500">{r.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">
                  {r.hire_date ? new Date(r.hire_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </td>
                <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">
                  {(r.license_states ?? []).join(", ") || "—"}
                </td>
                <td className="px-4 py-3 text-right font-display text-[14px] text-navy-800">{fmt(r.mtd)}</td>
                <td className="px-4 py-3 text-right font-display text-[14px] text-navy-800">{fmt(r.ytd)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-[0.12em] ${STATUS_PILL[r.status] ?? "bg-charcoal-500/15 text-charcoal-500"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger className="text-charcoal-500 hover:text-navy-800">
                      <MoreVertical size={16} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>View Profile</DropdownMenuItem>
                      {r.status === "pending" && (
                        <DropdownMenuItem onClick={() => void approve(r)}>Approve</DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => void resend(r)}>
                        Resend access link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSuspendTarget(r)} className="text-danger">
                        Suspend
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => void openReassign(r)}>Reassign clients</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-danger" onClick={() => setDeleteTarget(r)}>
                        Delete account
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend {suspendTarget?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The strategist will lose portal access immediately. Assigned clients remain in place until reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => suspendTarget && void suspend(suspendTarget)}>Suspend</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the strategist's account, profile, and login.
              Their assigned clients and leads will be unassigned. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a New Strategist</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Full name (optional)</label>
            <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)}
              placeholder="Jane Doe" className="border-bronze-600/30" />
            <label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Email</label>
            <Input
              type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="strategist@example.com" className="border-bronze-600/30"
            />
            <p className="font-sans text-[11px] text-charcoal-500">
              A secure invitation will be emailed. When they accept, the strategist role is granted automatically.
            </p>
          </div>
          <DialogFooter>
            <PrimaryButton onClick={() => void invite()} disabled={inviteSending || !inviteEmail}>
              {inviteSending ? "Sending…" : "Send Invitation"}
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!reassignTarget} onOpenChange={(o) => !o && setReassignTarget(null)}>
        <SheetContent className="bg-cream-50">
          <SheetHeader>
            <SheetTitle className="font-display text-navy-800">
              Reassign clients of {reassignTarget?.full_name}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-5">
            <div>
              <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Assigned clients · {reassignClients.length}</p>
              <ul className="mt-2 max-h-64 overflow-y-auto rounded-[4px] border border-bronze-600/20 bg-white divide-y divide-bronze-600/10">
                {reassignClients.length === 0 && (
                  <li className="px-3 py-4 text-center font-sans text-[12px] text-charcoal-500">No clients assigned.</li>
                )}
                {reassignClients.map((c) => (
                  <li key={c.id} className="px-3 py-2 font-sans text-[13px] text-charcoal-900">{c.full_name}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Reassign to strategist</p>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger className="mt-1 bg-white border-bronze-600/30"><SelectValue placeholder="Select strategist…" /></SelectTrigger>
                <SelectContent>
                  {rows.filter((a) => a.status === "active" && a.user_id !== reassignTarget?.user_id).map((a) => (
                    <SelectItem key={a.user_id} value={a.user_id}>{a.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <PrimaryButton onClick={() => void applyReassign()}
              disabled={reassignBusy || !reassignTo || reassignClients.length === 0}>
              {reassignBusy ? "Reassigning…" : `Reassign ${reassignClients.length} client(s)`}
            </PrimaryButton>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}